/**
 * Clears the launch splash (the `#splash` block in index.html) once the app has
 * painted underneath it.
 *
 * The splash is only ever shown when the app is launched from the home screen,
 * where iOS has just shown the same picture as its launch image - so this is the
 * one moment on screen where the game gets to say what it is. It deals: the top
 * four cards come off the deck to the four corners, as to four players, and the
 * table lifts away under them to leave the app.
 *
 * It is NOT the screen transition's deal (`ui/transitions/`), deliberately. That
 * one throws a spread of real card faces rendered by the Pixi card code, and at
 * launch nothing has rendered them yet - pulling that renderer in front of the
 * first paint is exactly what `main.tsx` goes out of its way not to do. These
 * cards are already on the page as plain SVG.
 *
 * Same rule as the transitions, though: the animation is decoration over a
 * TIMER. The element is removed by a `setTimeout` scheduled here, never by an
 * animation's completion, so an animation that never finishes (a backgrounded
 * page does not tick them) cannot leave a table sitting over the app. index.html
 * also removes it on its own after 15s, in case this never runs at all.
 */

const CARD_MS = 460;
const STAGGER_MS = 80;
const DEALT = 4;
const FADE_MS = 280;
/** When the table starts lifting: as the last dealt card is well on its way. */
const FADE_AT_MS = STAGGER_MS * (DEALT - 1) + CARD_MS * 0.55;

/** Where each dealt card heads, as a multiple of the splash's own size. */
const SEATS: ReadonlyArray<[number, number, number]> = [
  [-0.62, -0.58, -38],
  [0.62, -0.58, 32],
  [0.62, 0.62, -26],
  [-0.62, 0.62, 40],
];

/**
 * How long the deck sits on the felt after the publisher intro has lifted off
 * it, before the deal. Without it the deck is revealed already moving.
 */
const DECK_BEAT_MS = 400;

declare global {
  interface Window {
    /** performance.now() time the A Casual Place intro is gone (index.html). */
    __acpIntroUntil?: number;
  }
}

let dismissed = false;

/**
 * The splash opens on the publisher intro (the `acp-intro` block, generated by
 * a-casual-place/scripts/build-logo.mjs): black, the logo, black again, then it
 * lifts off the deck. The app is usually ready long before that ends, so the
 * deal waits for it - by timer, like everything else here.
 */
export function dismissSplash(): void {
  if (dismissed) return;
  dismissed = true;
  const until = window.__acpIntroUntil;
  const wait = until == null ? 0 : until + DECK_BEAT_MS - performance.now();
  if (wait > 0) window.setTimeout(deal, wait);
  else deal();
}

function deal(): void {

  const root = document.documentElement;
  const splash = document.getElementById("splash");
  if (!splash || !root.classList.contains("splash-on")) {
    splash?.remove();
    return;
  }

  const finish = () => {
    splash.remove();
    root.classList.remove("splash-on");
  };

  // Taps go to the app from the first frame of the exit, not the last.
  splash.style.pointerEvents = "none";

  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (reduced || typeof splash.animate !== "function") {
    splash.animate?.([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: "forwards" });
    window.setTimeout(finish, 220);
    return;
  }

  const { width, height } = splash.getBoundingClientRect();
  const cards = Array.from(splash.querySelectorAll<SVGElement>(".splash-card")).reverse();
  cards.slice(0, DEALT).forEach((card, i) => {
    const [fx, fy, turn] = SEATS[i];
    const base = card.style.transform || "translate(0px, 0px)";
    card.animate(
      [
        { transform: `${base} translate(0px, 0px) rotate(0deg)` },
        // A card is lifted before it is thrown: a small rise off the deck first.
        { transform: `${base} translate(0px, -6px) rotate(${turn * 0.08}deg)`, offset: 0.12 },
        { transform: `${base} translate(${fx * width}px, ${fy * height}px) rotate(${turn}deg)` },
      ],
      {
        duration: CARD_MS,
        delay: i * STAGGER_MS,
        easing: "cubic-bezier(0.5, 0, 0.25, 1)",
        fill: "forwards",
      },
    );
  });

  splash.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: FADE_MS,
    delay: FADE_AT_MS,
    easing: "ease-in",
    fill: "forwards",
  });
  window.setTimeout(finish, FADE_AT_MS + FADE_MS + 40);
}
